import { Component, type ReactNode } from 'react'
import { widgetRegistry } from './WidgetRegistry'
import { FallbackWidget } from './FallbackWidget'
import type { WidgetRendererProps, WidgetActionHandler } from '@/types'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

/**
 * Error boundary to catch widget rendering errors
 */
class WidgetErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Widget rendering error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }
    return this.props.children
  }
}

/**
 * WidgetRenderer dynamically renders widgets based on their type.
 * It validates widget data against registered schemas and handles errors gracefully.
 */
export function WidgetRenderer({ type, data, onAction }: WidgetRendererProps) {
  // Check if widget type is registered
  if (!widgetRegistry.has(type)) {
    console.warn(`Unknown widget type: ${type}`)
    return <FallbackWidget type={type} data={data} />
  }

  // Validate widget data
  const validation = widgetRegistry.validate(type, data)
  if (!validation.success) {
    console.warn(`Widget validation failed for type "${type}":`, validation.error)
    return <FallbackWidget type={type} data={data} />
  }

  // Get the registered component
  const registration = widgetRegistry.get(type)
  if (!registration) {
    return <FallbackWidget type={type} data={data} />
  }

  const WidgetComponent = registration.component

  // Create action handler that includes widget context
  const handleAction: WidgetActionHandler = (action, actionData) => {
    console.log(`Widget action triggered: ${action}`, actionData)
    onAction?.(action, actionData)
  }

  return (
    <WidgetErrorBoundary
      fallback={<FallbackWidget type={type} data={data} />}
    >
      <WidgetComponent data={validation.data} onAction={handleAction} />
    </WidgetErrorBoundary>
  )
}
